import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';
import { useAdminImage } from '@/hooks/useAdminImage';

const HERO_DEFAULT = 'https://readdy.ai/api/search-image?query=Luxurious%20modern%20premium%20medical%20aesthetic%20clinic%20interior%2C%20grand%20reception%20lobby%20with%20warm%20golden%20ambient%20light%2C%20polished%20white%20marble%20surfaces%2C%20elegant%20curved%20architecture%2C%20lush%20indoor%20green%20plants%2C%20sophisticated%20calming%20atmosphere%2C%20high%20end%20editorial%20interior%20photography%2C%20ultra%20sharp%208K&width=1800&height=1040&seq=services-hero-2026&orientation=landscape';

const HAIR_DEFAULT = 'https://readdy.ai/api/search-image?query=Handsome%20confident%20man%20with%20full%20thick%20natural%20looking%20dark%20hair%2C%20well%20groomed%20professional%20appearance%2C%20soft%20warm%20studio%20lighting%2C%20clean%20neutral%20background%2C%20natural%20dense%20hairline%20close%20up%2C%20premium%20grooming%20editorial%20portrait%2C%20sharp%20focus%2C%20ultra%20realistic&width=800&height=600&seq=service-card-hair-2026&orientation=landscape';

const DENTAL_DEFAULT = 'https://readdy.ai/api/search-image?query=Close%20up%20portrait%20of%20beautiful%20woman%20with%20perfect%20bright%20white%20smile%20and%20flawless%20teeth%2C%20professional%20dental%20beauty%20photography%2C%20soft%20warm%20studio%20lighting%2C%20warm%20skin%20tones%2C%20clean%20light%20background%2C%20natural%20radiant%20look%2C%20high%20end%20cosmetic%20dentistry%20result%2C%20editorial%20style%2C%20ultra%20sharp%20detail&width=800&height=600&seq=service-card-dental-2026&orientation=landscape';

const SURGERY_DEFAULT = 'https://readdy.ai/api/search-image?query=Elegant%20beautiful%20woman%20with%20refined%20facial%20features%2C%20natural%20flawless%20glowing%20skin%2C%20soft%20warm%20studio%20lighting%2C%20luxury%20beauty%20photography%2C%20graceful%20expression%2C%20premium%20aesthetic%20medicine%20result%2C%20sophisticated%20editorial%20portrait%2C%20cream%20and%20warm%20neutral%20background%2C%20subtle%20anti%20aging%20beauty%20look&width=800&height=600&seq=service-card-surgery-2026&orientation=landscape';

const BOTOX_DEFAULT = 'https://readdy.ai/api/search-image?query=Aesthetic%20medicine%20treatment%20room%20with%20warm%20lighting%20professional%20medical%20equipment%20elegant%20cream%20interior%20minimalist%20design%20editorial%20photography&width=800&height=600&seq=svc-botox-1&orientation=landscape';

export default function ServicesPage() {
  const { t } = useTranslation();

  const heroBg = useAdminImage('services', 'images.services.hero_bg', HERO_DEFAULT);
  const hairImg = useAdminImage('services', 'images.services.hair_img', HAIR_DEFAULT);
  const dentalImg = useAdminImage('services', 'images.services.dental_img', DENTAL_DEFAULT);
  const surgeryImg = useAdminImage('services', 'images.services.surgery_img', SURGERY_DEFAULT);
  const botoxImg = useAdminImage('services', 'images.services.botox_img', BOTOX_DEFAULT);

  const services = [
    {
      slug: 'dental',
      icon: 'ri-tooth-line',
      titleKey: 'services.dental.title',
      descKey: 'services.dental.desc',
      countKey: 'services.dental.count',
      imageUrl: dentalImg,
    },
    {
      slug: 'surgery',
      icon: 'ri-heart-pulse-line',
      titleKey: 'services.surgery.title',
      descKey: 'services.surgery.desc',
      countKey: 'services.surgery.count',
      imageUrl: surgeryImg,
    },
    {
      slug: 'hair',
      icon: 'ri-user-smile-line',
      titleKey: 'services.hair.title',
      descKey: 'services.hair.desc',
      countKey: 'services.hair.count',
      imageUrl: hairImg,
    },
  ];

  return (
    <>
      <Head
        title={t('seo.services.title')}
        metas={[
          { name: 'description', content: t('seo.services.description') },
          { name: 'keywords', content: t('seo.services.keywords') },
          { property: 'og:title', content: t('seo.services.title') },
          { property: 'og:description', content: t('seo.services.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/services' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: t('seo.services.title') },
          { name: 'twitter:description', content: t('seo.services.description') },
          { name: 'last-modified', content: '2026-06-11' },
        ]}
        links={[
          { rel: 'canonical', href: 'https://estevaclinic.com/services' },
        ]}
      />

      <main>
        <Navbar />

        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[520px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${heroBg})` }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/60"></div>
          <div className="relative z-10 max-w-7xl mx-auto text-center px-6">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white/90 text-xs tracking-[0.2em] uppercase font-label border border-white/20">
              {t('services.tag')}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mt-6 leading-[1.1]">
              {t('services.heading')}
            </h1>
            <p className="mt-4 text-white/70 text-sm md:text-base max-w-xl mx-auto font-body leading-relaxed">
              {t('services.hero_subtitle')}
            </p>
          </div>
        </section>

        {/* Service Cards */}
        <section className="py-20 md:py-28 px-6 md:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
              {services.map((service) => (
                <Link
                  key={service.slug}
                  to="/pricing"
                  className="group relative rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-all duration-500 cursor-pointer no-underline flex flex-col"
                >
                  <div className="relative h-56 md:h-64 overflow-hidden">
                    <img
                      src={service.imageUrl}
                      alt={t(service.titleKey)}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm border border-white/20 group-hover:bg-primary-500/80 group-hover:border-primary-400 transition-all duration-500">
                      <i className={`${service.icon} text-lg text-white`}></i>
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex-1 flex flex-col">
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {t(service.titleKey)}
                    </h3>
                    <p className="mt-2 text-xs md:text-sm text-foreground-500 leading-relaxed font-body flex-1">
                      {t(service.descKey)}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 text-xs font-label text-primary-600 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                      {t(service.countKey)}
                      <span className="text-foreground-300">·</span>
                      {t('gallery.view_details')}
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 px-6 md:px-10 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900">
              {t('services.page_cta_title')}
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-500 font-body leading-relaxed">
              {t('services.page_cta_subtitle')}
            </p>
            <Link
              to="/booking"
              className="mt-7 inline-block px-8 py-3.5 rounded-full bg-primary-500 text-white text-sm tracking-[0.1em] uppercase font-label hover:bg-primary-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
            >
              {t('booking.cta')}
            </Link>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}